import React, { useEffect, useRef, useState } from "react";

type TextFieldType = {
  placeholder: string;
  highlightText?: string;
  title?: string;
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  className?: string;
  labelClassName?: string; 
  type?: string;
  pattern?: string;
  helpText?: string;
  showHelpText?: boolean;
  isError?: boolean;
  errorText?: string;
  errorClassName?: string;
  focus?: boolean;
  focusPriority?: number;
  style?: React.CSSProperties;
  successText?: string;
  isSuccess?: boolean;
  custumFunction?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  disabled?: boolean;
};

// https://www.figma.com/design/xnr558XUCXb4vkBy6eAif7/%5B%EB%9D%BC%EC%9D%B4%EB%B8%8C%EB%9F%AC%EB%A6%AC%5D-Potoo-Library?node-id=148-1126&t=3lunUvpzgaQBTI5S-4
const TextField = (props: TextFieldType) => {
  const {
    placeholder,
    highlightText,
    title,
    input,
    setInput,
    className,
    labelClassName,  
    type,
    pattern,
    helpText,
    showHelpText = false,
    isError = false,
    errorText,
    errorClassName = "",
    focus = false,
    focusPriority = 0,
    custumFunction,
    successText,
    isSuccess,
    onBlur,
    disabled = false,
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const handleFocus = () => setIsFocused(true); // 포커스 되면 포커스 상태 변경
    const handleBlur = () => setIsFocused(false); // 블러(포커스 해제) 되면 포커스 상태 변경

    const input = inputRef.current;
    if (input && focus) {
      input.addEventListener("focus", handleFocus);
      input.addEventListener("blur", handleBlur);
      
      if (focusPriority === 0) {
        // textfield가 두개 이상인 경우 우선순위가 높은 field에 가장 첫번째로 focus 되도록 설정하기 위함.
        input.focus();
      }
     }

    // clean event listeners
    return () => {
      if (input && focus) {
        input.removeEventListener("focus", handleFocus);
        input.removeEventListener("blur", handleBlur);
      }
    };
  }, []);

  // 현재 isFocused 상태 확인용 코드
  // useEffect(() => {
  //   console.log("현재 isFocused 상태:", isFocused);
  // }, [isFocused]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        textAlign: "start",
        gap: "4px",
        width: "100%",
      }}
      {...props}
    >
      {title && <span className={`body1-700 gray-800 ${labelClassName}`}>{title}</span>}

      <input
        className={`text-field ${isError ? "is-error" : ""} ${className} ${disabled ? "disabled" : ""}`}
        placeholder={placeholder}
        ref={inputRef}
        value={input}
        type={type}
        disabled={disabled}
        pattern={pattern}
        onChange={(e) => {
          // customFunction이 있으면 실행
          if (custumFunction) {
            custumFunction(e);
          } else {
            // customFunction이 없으면 input을 업데이트
            if (inputRef.current) setInput(inputRef.current.value);
          }
        }}
        onBlur={onBlur}
      />
      {!isError && (showHelpText || isFocused) && helpText && input.length === 0 && (
        <span className="caption-500 blue-500">{helpText}</span>
      )}
      {isError && errorText && (
        <span className={`caption-500 red-500 ${errorClassName}`}>{errorText}</span>
      )}
      {isSuccess && successText && <span className="caption-500 green-500">{successText}</span>}
    </div>
  );
};

export { TextField };
